import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

// Backs the Access Management tab (Super Admin only — see routes/accessManagement.js).

const fetchCatalog = async () => {
  const { data } = await axios.get("/access-management/catalog");
  return data;
};

export const useAccessCatalog = () =>
  useQuery({
    queryKey: ["access-management-catalog"],
    queryFn: fetchCatalog,
    staleTime: 10 * 60 * 1000,
  });

const buildUserParams = ({ q, role, status, page, limit, sortBy, sortDir, dormant }) => {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (role && role !== "all") params.set("role", role);
  if (status && status !== "all") params.set("status", status);
  if (page) params.set("page", page);
  if (limit) params.set("limit", limit);
  if (sortBy) {
    params.set("sortBy", sortBy);
    params.set("sortDir", sortDir || "desc");
  }
  if (dormant) params.set("dormant", "true");
  return params;
};

const fetchUsers = async (opts = {}) => {
  const params = buildUserParams(opts);
  const qs = params.toString();
  const { data } = await axios.get(`/access-management/users${qs ? `?${qs}` : ""}`);
  return data;
};

// Paginated + role-filtered page of users for the table. `keepPreviousData` keeps the old
// rows on screen (instead of flashing a spinner) while a page/filter change is in flight.
export const useAccessUsers = (opts = {}) =>
  useQuery({
    queryKey: [
      "access-management-users",
      opts.q || "",
      opts.role || "all",
      opts.status || "all",
      opts.page || 1,
      opts.limit || 20,
      opts.sortBy || "",
      opts.sortDir || "",
      opts.dormant ? "dormant" : "",
    ],
    queryFn: () => fetchUsers(opts),
    keepPreviousData: true,
  });

// Downloads the roster as .xlsx. The endpoint streams the file itself (rather than a
// signed URL) so it works in dev too, where no S3 bucket is configured — which means the
// browser save has to be triggered here from the blob.
export const downloadAccessRoster = async (opts = {}) => {
  const params = buildUserParams({ ...opts, page: undefined, limit: undefined });
  const qs = params.toString();
  const response = await axios.get(`/access-management/users/export${qs ? `?${qs}` : ""}`, {
    responseType: "blob",
  });

  const disposition = response.headers["content-disposition"] || "";
  const filename = disposition.match(/filename="(.+?)"/)?.[1] || "access-roster.xlsx";

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return { filename, rows: Number(response.headers["x-export-rows"]) || 0 };
};

// The full, unfiltered roster for the "Manages these users" picker — only needed once a
// Manage dialog is actually open, so it's fetched lazily (`enabled`) rather than on every
// page load, and kept a while since the org roster doesn't change from one dialog to the
// next.
export const useAllAccessUsers = (enabled) =>
  useQuery({
    queryKey: ["access-management-users", "", "all", "all-for-picker"],
    // 200 is the backend's own cap on `limit` (see PAGE_SIZE_OPTIONS' comment in
    // AccessManagementPage.jsx) — asking for more would silently come back truncated.
    queryFn: () => fetchUsers({ limit: 200 }),
    enabled,
    staleTime: 60 * 1000,
  });

// Creates an internal account from the "New user" drawer. The password goes over the
// wire in plain text and is bcrypt-hashed server-side — see POST
// /api/access-management/users in routes/accessManagement.js.
const createUser = async (payload) => {
  const { data } = await axios.post("/access-management/users", payload);
  return data;
};

const updateRole = async ({ id, role }) => {
  const { data } = await axios.patch(`/access-management/users/${id}/role`, { role });
  return data;
};

const updateStatus = async ({ id, isActive }) => {
  const { data } = await axios.patch(`/access-management/users/${id}/status`, { isActive });
  return data;
};

const updatePermissions = async ({ id, permissions }) => {
  const { data } = await axios.patch(`/access-management/users/${id}/permissions`, { permissions });
  return data;
};

const updateManages = async ({ id, manageUsers }) => {
  const { data } = await axios.patch(`/access-management/users/${id}/manages`, { manageUsers });
  return data;
};

const updateBalance = async ({ id, delta, balance }) => {
  const payload = typeof balance === "number" ? { balance } : { delta };
  const { data } = await axios.patch(`/access-management/users/${id}/balance`, payload);
  return data;
};

// Enable/disable many at once — either the checked ids, or everything matching the
// current view (applyToFilter), which is how "disable all dormant accounts" avoids
// paging through them 20 at a time.
const bulkStatus = async ({ isActive, userIds, applyToFilter, q, role, status, dormant }) => {
  const { data } = await axios.patch("/access-management/users/bulk", {
    isActive,
    ...(applyToFilter ? { applyToFilter: true, q, role, status, dormant } : { userIds }),
  });
  return data;
};

const forceLogout = async ({ id }) => {
  const { data } = await axios.post(`/access-management/users/${id}/force-logout`);
  return data;
};

const addManagedBy = async ({ id, managerId }) => {
  const { data } = await axios.post(`/access-management/users/${id}/managed-by`, { managerId });
  return data;
};

const removeManagedBy = async ({ id, managerId }) => {
  const { data } = await axios.delete(`/access-management/users/${id}/managed-by/${managerId}`);
  return data;
};

function useAccessMutation(mutationFn) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["access-management-users"] }),
  });
}

export const useCreateUser = () => useAccessMutation(createUser);
export const useUpdateUserRole = () => useAccessMutation(updateRole);
export const useUpdateUserStatus = () => useAccessMutation(updateStatus);
export const useUpdateUserPermissions = () => useAccessMutation(updatePermissions);
export const useUpdateUserManages = () => useAccessMutation(updateManages);
// Doesn't change role/status/permissions, so no need to invalidate the users list.
export const useForceLogout = () => useMutation({ mutationFn: forceLogout });
export const useUpdateBalance = () => useAccessMutation(updateBalance);
export const useBulkStatus = () => useAccessMutation(bulkStatus);
export const useAddManagedBy = () => useAccessMutation(addManagedBy);
export const useRemoveManagedBy = () => useAccessMutation(removeManagedBy);
